import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { Spacer, Select } from '@zillow/constellation';
import { Col, Row } from '@zillow/zrs-comet';

import { UserSelectors, AnalyticsSelectors, AppSelectors } from 'app/shared/selectors';
import AnalyticsActions from 'app/shared/actions/AnalyticsActions';
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';
import HeadWrapper from 'app/shared/components/HeadWrapper';
import InvoiceGraph from 'app/shared/components/Graphs/InvoiceGraph';
import LoadingConstants from 'app/shared/constants/LoadingConstants';
import api from 'app/shared/utils/api';

import PropertyGraph from './PropertyGraph';
import ViewGraph from './ViewGraph';
import LeadClickGraph from './LeadClickGraph';
import ReviewGraph from './ReviewGraph';

const propTypes = {
    graphs: PropTypes.object.isRequired,
    overviewFilter: PropTypes.object.isRequired,
    cookieEnabledServices: PropTypes.object.isRequired,
};

const sortPropertyNames = (listings) => {
    return listings.sort((a, b) => {
        if (!a.name && b.name) return b;
        if (a.name && !b.name) return a;
        if (!a.name && !b.name) return 0;
        return a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
    });
};

class OverviewPage extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isLoadingInvoicesGraph: true,
            isLoadingListings: true,
            yearlyData: [],
            yearlyDataLoadingState: LoadingConstants.INITIAL,
        };
        this.handlePropertyChange = this.handlePropertyChange.bind(this);
    }

    componentDidMount() {
        this.getGraphs();
    }

    getGraphs(filterKey = '') {
        const { dispatch } = this.props;
        this.setState({
            isLoadingInvoicesGraph: true,
            filterKey,
            yearlyDataLoadingState: LoadingConstants.LOADING,
        });
        const params = {};
        if (filterKey) {
            const matchedListingId = filterKey.match(/listingId:(.*)/);
            const matchedRocketshipFilter = filterKey.match(/rocketshipFilter:(.*)/);
            if (matchedListingId != null) {
                params.listingId = matchedListingId[1];
            } else if (matchedRocketshipFilter != null) {
                params.rocketshipFilter = matchedRocketshipFilter[1];
            }
        }

        dispatch(AnalyticsActions.getOverviewFilter())
            .then(() => this.setState({ isLoadingListings: false }))
            .catch(() => this.setState({ isLoadingListings: false }));
        dispatch(AnalyticsActions.getMonthlyOverviewStats(params))
            .then(() => this.setState({ isLoadingInvoicesGraph: false }))
            .catch(() => this.setState({ isLoadingInvoicesGraph: false }));
        dispatch(api.users.analytics.getYearlyOverview(params))
            .then((res) => {
                if (res.success) {
                    this.setState({
                        yearlyData: res.response,
                        yearlyDataLoadingState: LoadingConstants.SUCCESS,
                    });
                } else {
                    this.setState({
                        yearlyData: [],
                        yearlyDataLoadingState: LoadingConstants.ERROR,
                    });
                }
            })
            .catch(() => {
                this.setState({
                    yearlyData: [],
                    yearlyDataLoadingState: LoadingConstants.ERROR,
                });
            });
    }

    handlePropertyChange(e) {
        this.getGraphs(e.target.value);
    }

    renderContent() {
        const {
            isLoadingListings,
            isLoadingInvoicesGraph,
            filterKey,
            yearlyDataLoadingState,
            yearlyData,
        } = this.state;
        const {
            graphs,
            overviewFilter,
            cookieEnabledServices,
        } = this.props;
        let filterOptions = [
            <option key="" value="">All properties</option>,
            ...sortPropertyNames(overviewFilter.listings).map(listing => (
                <option key={listing.listingId} value={`listingId:${listing.listingId}`}>{listing.name}</option>
            )),
        ];

        filterOptions = filterOptions.concat(...overviewFilter.rocketshipAccounts.map(account => (
            <option
                key={account.zillowAccountId}
                value={`rocketshipFilter:${account.customerId}_${account.zillowAccountId}`}
            >
                {account.billingAccountName}
            </option>
        )));

        const isLoading = isLoadingListings
            || (yearlyDataLoadingState !== LoadingConstants.SUCCESS)
            || isLoadingInvoicesGraph;

        const isAllProperties = !filterKey;

        return (
            <React.Fragment>
                {filterOptions.length > 2 && (
                    <Spacer marginY="sm">
                        <Row>
                            <Col md={12} lg={5} xl={5}>
                                <Select
                                    name="sort"
                                    value={filterKey}
                                    onChange={this.handlePropertyChange}
                                    disabled={isLoading}
                                >
                                    {filterOptions}
                                </Select>
                            </Col>
                        </Row>
                    </Spacer>
                )}
                {isAllProperties && (
                    <PropertyGraph
                        data={yearlyData}
                        isLoading={yearlyDataLoadingState !== LoadingConstants.SUCCESS}
                    />
                )}
                <ViewGraph
                    data={yearlyData}
                    isLoading={yearlyDataLoadingState !== LoadingConstants.SUCCESS}
                    showTabs={isAllProperties}
                />
                <LeadClickGraph
                    data={yearlyData}
                    isLoading={yearlyDataLoadingState !== LoadingConstants.SUCCESS}
                    showTabs={isAllProperties}
                />
                <ReviewGraph
                    data={yearlyData}
                    isLoading={yearlyDataLoadingState !== LoadingConstants.SUCCESS}
                />
                <InvoiceGraph
                    data={graphs.invoice}
                    isLoading={isLoadingInvoicesGraph}
                    separateLeaseConnect={cookieEnabledServices.SEPARATE_LEASE_CONNECT}
                />
            </React.Fragment>
        );
    }

    render() {
        return (
            <AuthCheckWrapper requireStrongAuthentication={false}>
                <div className="OverviewPage">
                    <HeadWrapper title="Analytics Overview" />
                    {this.renderContent()}
                </div>
            </AuthCheckWrapper>
        );
    }
}

const mapStateToProps = state => ({
    user: UserSelectors.makeUser(state),
    graphs: AnalyticsSelectors.makeGraphs(state),
    overviewFilter: AnalyticsSelectors.makeOverviewFilter(state),
    cookieEnabledServices: AppSelectors.makeCookieEnabledServices(state),
});

OverviewPage.propTypes = propTypes;

export { OverviewPage };
export default connect(mapStateToProps)(OverviewPage);
